import { type IWidgetDirectiveType } from '@framework/engine/schema/directives/IWidgetDirectiveType.ts';
import { type IBaseWidget } from '@framework/engine/schema/widgets/IBaseWidget.ts';
import { isInputWidget } from '@framework/engine/schema/widgets/repository/inputs/isInputWidget.ts';
import { WidgetType } from '@framework/engine/schema/widgets/Widget.ts';
import { type yup } from '@framework/validation/initYup.ts';
import { type YupAnyObjectSchema } from '@framework/validation/YupObjectSchema.ts';
import { isDate, set } from 'lodash';
import { type Schema } from 'yup';
import {
  type IOmissisDirective,
  OmissisDirectiveSchema,
} from './IOmissisDirective.ts';
import { OmissisDirectiveConfigComponent } from './OmissisDirectiveConfigComponent.tsx';
import {
  OMISSIS_CONTEXT_KEY,
  OMISSIS_REPLACE_TEXT,
} from './omissisConstants.ts';

export function replaceOmissisValue(value: any): any {
  if (value == null) return value;
  if (typeof value === 'string') return OMISSIS_REPLACE_TEXT;
  if (isDate(value)) return OMISSIS_REPLACE_TEXT;
  if (typeof value === 'number') return OMISSIS_REPLACE_TEXT;
  if (typeof value === 'boolean') return null;
  if (Array.isArray(value)) return [];
  // Ogni campo interno viene comunque modificato
  // attraverso l'applicazione del transform a tutti i campi interni
  if (typeof value === 'object') {
    return Object.keys(value).reduce((acc, key) => {
      return set(acc, key, replaceOmissisValue(value[key]));
    }, {});
  }
}

function isObjectSchema(schema: yup.AnySchema): schema is YupAnyObjectSchema {
  return schema.type === 'object';
}

function tranformOmissisSchema(schema: yup.AnySchema): yup.AnySchema {
  return schema.transform(v => {
    return replaceOmissisValue(v);
  });
}

export const OmissisDirectiveType: IWidgetDirectiveType<IOmissisDirective> = {
  name: 'omissis',
  key: 'omissis',
  allowsWidget: (widget, widgetType) =>
    isInputWidget(widget) && widget.type !== WidgetType.Repeatable,
  configComponent: OmissisDirectiveConfigComponent,
  configSchema: OmissisDirectiveSchema,
  formSchemaModifier: ({ directiveConfig, schema, widget, widgetType }) => {
    return schema.when(
      [`$${OMISSIS_CONTEXT_KEY}`],
      ([shouldHideOmissis]: boolean[], schema) => {
        if (!shouldHideOmissis) {
          // Se non stiamo _sostituendo_ i valori con l'omissis, abbiamo
          // comunque bisogno di sapere se questo campo _prevede_ dei
          // dati sensibili o meno. Lo salviamo quindi nel meta dello schema.
          return schema.meta({ hasOmissis: directiveConfig.enabled });
        }

        if (!directiveConfig.enabled) return schema;

        // Se ci troviamo in un object schema, dobbiamo applicare la trasformazione anche a
        // tutti i sottocampi.
        // e.g. `<code>@object` di AsyncValue e AsyncSelect
        if (isObjectSchema(schema)) {
          Object.keys(schema.fields).forEach(key => {
            schema.fields[key] =
              // Facciamo un cast a schema per evitare errori di typing
              tranformOmissisSchema(schema.fields[key] as Schema).meta({
                hasOmissis: directiveConfig.enabled,
              });
          });
        }

        return tranformOmissisSchema(schema)
          .notRequired()
          .meta({ hasOmissis: directiveConfig.enabled });
      },
    );
  },
  supportedSectionTypes: ['FRONTOFFICE', 'BACKOFFICE'],
};

/**
 * Estrae la configurazione dell'omissis all'interno della direttiva
 * di un widget.
 */
export function isOmissisEnabled(widget: IBaseWidget): boolean {
  return widget.directives?.omissis?.enabled ?? false;
}
